import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';
const CACHE_KEY = 'ekklesia_pricing';
// Cada cuánto se intenta refrescar mientras la pestaña vive.
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutos

// Lee la última respuesta de precios persistida (localStorage = sobrevive a cierres de pestaña).
function loadCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data) return null;
    return { data: parsed.data, ts: typeof parsed.ts === 'number' ? parsed.ts : 0 };
  } catch {
    return null;
  }
}

function saveCache(data, ts) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts }));
  } catch { /* ignore */ }
}

const PricingContext = createContext(null);

export function PricingProvider({ children }) {
  const initial = loadCached();
  // `data` es la última fuente conocida de precios (caché o respuesta fresca).
  const [data, setData] = useState(initial?.data ?? null);
  // `loading` solo bloquea cuando no hay NADA que mostrar todavía.
  const [loading, setLoading] = useState(!initial);
  // `stale` = estamos mostrando precios cacheados que no se han podido refrescar.
  const [stale, setStale] = useState(false);
  const dataRef = useRef(data);
  dataRef.current = data;

  const fetchPricing = useCallback(async () => {
    if (!API_URL) {
      setLoading(false);
      if (dataRef.current) setStale(true);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/public/pricing-info`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const now = Date.now();
      setData(json);
      setStale(false);
      saveCache(json, now);
    } catch {
      // Fallo de red/API: si tenemos datos previos (caché), los seguimos mostrando como "stale".
      if (dataRef.current) setStale(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
    const id = setInterval(fetchPricing, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchPricing]);

  const hasData = !!data;
  const value = {
    // Solo "cargando" si aún no hay datos (ni caché ni respuesta).
    loading: loading && !hasData,
    // Solo "error" si no hay absolutamente nada que mostrar.
    error: !hasData && !loading,
    // Mostrando precios cacheados sin poder confirmar que están al día.
    stale,
    founderActive: data?.founder_active ?? false,
    slotsTotal: data?.slots_total ?? 15,
    slotsUsed: data?.slots_used ?? 0,
    slotsRemaining: data?.slots_remaining ?? 0,
    prices: data?.prices ?? null,
    annualAvailable: data?.annual_available ?? false,
    // Sin fallback hardcodeado: el precio de módulo viene del backend o es null.
    modulePrice: data?.module_price ?? null,
    ivaIncluded: data?.iva_included ?? true,
    refetch: fetchPricing,
  };

  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
}

export function usePricing() {
  const ctx = useContext(PricingContext);
  if (!ctx) throw new Error('usePricing must be used within PricingProvider');
  return ctx;
}
